import React, { useState, useEffect } from 'react';
import './blocks/ModernBlocks.css';

const WorkPage = () => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setProjects([
        {
          id: 1,
          title: "Luxury Residential Complex",
          client: "Sunset Heights Condominiums",
          description: "Complete security system installation for a 200-unit luxury residential complex including CCTV, access control, and 24/7 monitoring.",
          image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop",
          technologies: ["CCTV Systems", "Access Control", "Alarm Systems", "24/7 Monitoring", "Mobile App"],
          duration: "6 weeks",
          year: 2024,
          category: "residential",
          results: [
            "100% property coverage with HD cameras",
            "Reduced security incidents by 85%",
            "24/7 professional monitoring service",
            "Mobile app access for all residents"
          ]
        },
        {
          id: 2,
          title: "Corporate Office Security",
          client: "TechCorp Industries",
          description: "Advanced security system for a 50,000 sq ft corporate headquarters with biometric access control and integrated alarm systems.",
          image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop",
          technologies: ["Biometric Access", "CCTV Surveillance", "Alarm Integration", "Visitor Management", "Audit Trails"],
          duration: "4 weeks",
          year: 2024,
          category: "commercial",
          results: [
            "Enhanced building security with biometric access",
            "Complete visitor tracking and management",
            "Integrated alarm and CCTV systems",
            "Compliance with corporate security standards"
          ]
        },
        {
          id: 3,
          title: "Retail Store Security",
          client: "Premium Retail Chain",
          description: "Comprehensive security solution for a chain of 15 retail stores with loss prevention and customer safety features.",
          image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop",
          technologies: ["CCTV Systems", "Loss Prevention", "Alarm Systems", "Remote Monitoring", "Analytics"],
          duration: "8 weeks",
          year: 2024,
          category: "retail",
          results: [
            "Reduced theft incidents by 90%",
            "Improved customer safety",
            "Remote monitoring capabilities",
            "Detailed analytics and reporting"
          ]
        },
        {
          id: 4,
          title: "Industrial Warehouse Security",
          client: "Global Logistics Corp",
          description: "Large-scale security system for a 100,000 sq ft warehouse facility with perimeter protection and inventory monitoring.",
          image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&h=300&fit=crop",
          technologies: ["Perimeter Security", "CCTV Systems", "Access Control", "Motion Detection", "24/7 Monitoring"],
          duration: "10 weeks",
          year: 2024,
          category: "industrial",
          results: [
            "Complete perimeter protection",
            "Inventory loss prevention",
            "Employee access control",
            "24/7 security monitoring"
          ]
        },
        {
          id: 5,
          title: "Educational Institution",
          client: "Metropolitan University",
          description: "Campus-wide security system for a university with multiple buildings, parking areas, and student safety features.",
          image: "https://images.unsplash.com/photo-1523050854058-8df90110c9a1?w=400&h=300&fit=crop",
          technologies: ["Campus Security", "Access Control", "Emergency Systems", "Student Safety", "Mobile App"],
          duration: "12 weeks",
          year: 2024,
          category: "institutional",
          results: [
            "Enhanced campus safety",
            "Emergency response integration",
            "Student and staff protection",
            "Compliance with educational security standards"
          ]
        },
        {
          id: 6,
          title: "Healthcare Facility Security",
          client: "Regional Medical Center",
          description: "Specialized security system for a hospital with patient safety, staff protection, and emergency response integration.",
          image: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&h=300&fit=crop",
          technologies: ["Patient Safety", "Staff Protection", "Emergency Systems", "Access Control", "HIPAA Compliance"],
          duration: "8 weeks",
          year: 2024,
          category: "healthcare",
          results: [
            "Enhanced patient and staff safety",
            "HIPAA compliant security measures",
            "Emergency response integration",
            "Secure access to sensitive areas"
          ]
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const filters = [
    { id: 'all', label: 'All Projects', count: projects.length },
    { id: 'residential', label: 'Residential', count: projects.filter(p => p.category === 'residential').length },
    { id: 'commercial', label: 'Commercial', count: projects.filter(p => p.category === 'commercial').length },
    { id: 'retail', label: 'Retail', count: projects.filter(p => p.category === 'retail').length },
    { id: 'industrial', label: 'Industrial', count: projects.filter(p => p.category === 'industrial').length },
    { id: 'institutional', label: 'Institutional', count: projects.filter(p => p.category === 'institutional').length },
    { id: 'healthcare', label: 'Healthcare', count: projects.filter(p => p.category === 'healthcare').length }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const openModal = (project) => {
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
  };

  if (loading) {
    return (
      <div className="work-loading">
        <div className="loading-spinner"></div>
        <p>Loading our security projects...</p>
      </div>
    );
  }

  return (
    <div className="work-page">
      {/* Hero Section */}
      <div className="work-hero">
        <div className="work-hero-container">
          <h1 className="work-hero-title">Security Projects</h1>
          <p className="work-hero-subtitle">
            Explore our successful security installations and case studies across residential, 
            commercial, and institutional properties. Each project demonstrates our expertise 
            in delivering comprehensive security solutions with proven results.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="work-filters">
        <div className="work-filters-container">
          <div className="filters-wrapper">
            {filters.map(filter => (
              <button
                key={filter.id}
                className={`filter-button ${activeFilter === filter.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
                <span className="filter-count">({filter.count})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="work-projects">
        <div className="work-projects-container">
          <div className="masonry-grid">
            {filteredProjects.map((project, index) => (
              <div 
                key={project.id} 
                className="project-card"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => openModal(project)}
              >
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay">
                    <div className="project-overlay-content">
                      <h3>{project.title}</h3>
                      <p>{project.client}</p>
                    </div>
                  </div>
                </div>
                <div className="project-info">
                  <div className="project-content">
                    <div className="project-meta">
                      <span className="project-duration">{project.duration}</span>
                      <span className="project-year">{project.year}</span>
                    </div>
                    <h3 className="project-title">{project.title}</h3>
                    <p className="project-client">{project.client}</p>
                    <p className="project-description">{project.description}</p>
                    <div className="project-technologies">
                      {project.technologies.slice(0, 3).map((tech, idx) => (
                        <span key={idx} className="tech-tag">{tech}</span>
                      ))}
                      {project.technologies.length > 3 && (
                        <span className="tech-tag more">+{project.technologies.length - 3} more</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <div className="project-modal-overlay" onClick={closeModal}>
          <div className="project-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-content">
              <div className="modal-image-section">
                <img 
                  src={selectedProject.image} 
                  alt={selectedProject.title} 
                  className="modal-full-image"
                />
              </div>
              <div className="modal-info-section">
                <div className="modal-header">
                  <h2 className="modal-title">{selectedProject.title}</h2>
                </div>
                <div className="modal-body">
                  <div className="modal-stats">
                    <div className="stat-item">
                      <span className="stat-label">Client</span>
                      <span className="stat-value">{selectedProject.client}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Duration</span>
                      <span className="stat-value">{selectedProject.duration}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Year</span>
                      <span className="stat-value">{selectedProject.year}</span>
                    </div>
                  </div>
                  
                  <div className="modal-technologies-section">
                    <h4>Technologies Used:</h4>
                    <div className="modal-technologies">
                      {selectedProject.technologies.map((tech, idx) => (
                        <span key={idx} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>

                  <div className="modal-results">
                    <h4>Key Results:</h4>
                    <ul>
                      {selectedProject.results.map((result, idx) => (
                        <li key={idx}>{result}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="work-cta">
        <div className="work-cta-container">
          <h2>Ready to Start Your Security Project?</h2>
          <p>Get a free consultation and quote for your security installation.</p>
          <div className="work-cta-buttons">
            <a href="/contact" className="cta-button primary">
              Free Consultation
            </a>
            <a href="tel:+1234567890" className="cta-button secondary">
              Call Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkPage; 